import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

export const useConfetti = () => {
  const confettiRef = useRef<confetti.CreateTypes | null>(null);

  const playCelebrationSound = async () => {
    try {
      // Create AudioContext only when confetti is triggered
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Try to resume if suspended (modern browsers require user interaction)
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch (resumeError) {
          // If resume fails, audio won't play (browser restriction)
          // This is expected behavior - audio only plays after user interaction
          console.log('AudioContext suspended - audio will play on next trigger after user interaction');
          return;
        }
      }
      
      // Classic "win" sound - fanfare style ascending notes
      // C major arpeggio: C-E-G-C (higher octave)
      const winNotes = [
        { freq: 523.25, time: 0, duration: 0.2 },      // C5
        { freq: 659.25, time: 0.1, duration: 0.2 },    // E5
        { freq: 783.99, time: 0.2, duration: 0.2 },    // G5
        { freq: 1046.50, time: 0.3, duration: 0.4 },  // C6 (held longer)
      ];

      winNotes.forEach((note) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // Use a bright, crisp waveform for that classic win sound
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(note.freq, audioContext.currentTime + note.time);
        
        // Sharp attack, sustained, then quick release - increased volume
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + note.time);
        gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + note.time + 0.01);
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime + note.time + note.duration * 0.7);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + note.time + note.duration);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.start(audioContext.currentTime + note.time);
        oscillator.stop(audioContext.currentTime + note.time + note.duration);
      });

      // Add a final triumphant high note
      setTimeout(() => {
        const finalOscillator = audioContext.createOscillator();
        const finalGain = audioContext.createGain();
        
        finalOscillator.type = 'triangle';
        finalOscillator.frequency.setValueAtTime(1318.51, audioContext.currentTime); // E6
        
        finalGain.gain.setValueAtTime(0, audioContext.currentTime);
        finalGain.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.02);
        finalGain.gain.setValueAtTime(0.5, audioContext.currentTime + 0.15);
        finalGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        
        finalOscillator.connect(finalGain);
        finalGain.connect(audioContext.destination);
        
        finalOscillator.start();
        finalOscillator.stop(audioContext.currentTime + 0.4);
      }, 0.3);
      
    } catch (error) {
      console.error('Error playing celebration sound:', error);
      // Fallback: Classic win sound pattern
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // Resume if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Classic ascending win melody
        const winFrequencies = [
          { freq: 523.25, delay: 0 },    // C5
          { freq: 659.25, delay: 100 },   // E5
          { freq: 783.99, delay: 200 },   // G5
          { freq: 1046.50, delay: 300 },  // C6
          { freq: 1318.51, delay: 400 }, // E6
        ];
        
        winFrequencies.forEach((note) => {
          setTimeout(() => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.type = 'triangle';
            oscillator.frequency.setValueAtTime(note.freq, audioContext.currentTime);
            
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
            gainNode.gain.setValueAtTime(0.5, audioContext.currentTime + 0.15);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.3);
          }, note.delay);
        });
      } catch (e) {
        console.error('Audio playback not supported:', e);
      }
    }
  };

  const triggerConfetti = () => {
    // Play celebration sound (async but don't wait)
    playCelebrationSound().catch((error) => {
      console.error('Failed to play celebration sound:', error);
    });
    // Immediate big burst in the center
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { x: 0.5, y: 0.6 },
      colors: ['#FFD400', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'],
      zIndex: 10000,
      startVelocity: 45,
    });

    // Additional bursts from sides
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ['#FFD400', '#FF6B6B', '#4ECDC4'],
        zIndex: 10000,
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ['#45B7D1', '#FFA07A', '#98D8C8'],
        zIndex: 10000,
      });
    }, 100);

    // Final burst
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 360,
        origin: { x: 0.5, y: 0.5 },
        colors: ['#FFD400', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'],
        zIndex: 10000,
        startVelocity: 30,
      });
    }, 200);
  };

  return { triggerConfetti };
};

